/**
 * `/strategy` — every account the caller can read a strategy for.
 *
 * The SERVER decides the list (`GET /api/account-strategies`): Manager and
 * Admin see every account, anyone else the accounts they are on the team of.
 * An account is listed whether or not a strategy has been written for it yet.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AccountStrategyListRow } from '@menlo/shared';
import { useApiQuery } from '@/lib/queries';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { formatDateTime } from '@/lib/format';

export function StrategyList(): React.ReactElement {
  const list = useApiQuery<{ items: AccountStrategyListRow[] }>(
    ['account-strategies'],
    '/api/account-strategies',
  );
  const [term, setTerm] = useState('');

  const items = useMemo(() => {
    const all = list.data?.items ?? [];
    const needle = term.trim().toLowerCase();
    return needle === '' ? all : all.filter((r) => r.account_name.toLowerCase().includes(needle));
  }, [list.data, term]);

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Account strategy</h1>
          <p className="text-sm text-muted-foreground">
            One-page plans. Visible to managers and to each account&rsquo;s team.
          </p>
        </div>
        <label className="w-full sm:w-64">
          <span className="sr-only">Filter accounts</span>
          <Input
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            placeholder="Filter accounts…"
          />
        </label>
      </div>

      {list.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {list.error && <p className="text-sm text-red-700">{list.error.message}</p>}

      {list.data && (
        <ul className="divide-y rounded-md border bg-card">
          {items.map((row) => (
            <li key={row.account_id}>
              <Link
                to={`/strategy/${row.account_id}`}
                className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 hover:bg-accent"
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">{row.account_name}</span>
                  <span className="text-xs text-muted-foreground">
                    Owner: {row.owner_name ?? '—'}
                  </span>
                </span>
                <span className="flex items-center gap-2 text-xs text-muted-foreground">
                  {row.membership.isAccountOwner && <Badge variant="info">You own it</Badge>}
                  {row.strategy_updated_at === null ? (
                    <Badge variant="outline">Not started</Badge>
                  ) : (
                    <span>Updated {formatDateTime(row.strategy_updated_at)}</span>
                  )}
                </span>
              </Link>
            </li>
          ))}
          {items.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-muted-foreground">
              {term === ''
                ? 'No accounts yet. You see an account here once you own it, or own or support one of its opportunities.'
                : 'No account matches.'}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
